import React, {useEffect, useMemo, useState} from "react";
import {JobApplicationDetails, UpdatedJobApplication} from "@/data/entities/jobApplication";
import {Route} from "@/routing/Router";
import JobApplicationHeading from "./heading/JobApplicationHeading";
import {useNotifications, useResponsive} from "peggirkit";
import {breakpointEqualOrGreaterThan} from "peggirkit/dist/components/Util/ResponsiveUtils";
import JobApplicationFormDetails from "./details/JobApplicationFormDetails";
import JobApplicationMotivation from "@widgets/jobApplications/editor/motivation/JobApplicationMotivation";
import JobApplicationInternalNote from "./internalNote/JobApplicationInternalNote";
import {FormProvider, useForm, UseFormSetValue} from "react-hook-form";
import {useIntl} from "react-intl";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {SetStateFn} from "@/utils/SetStateUtils";
import {Fn} from "@/utils/FunctionUtils";
import {api} from "@/data/api";


const setJobApplicationValues = (
    jobApplication: JobApplicationDetails,
    setValue: UseFormSetValue<UpdatedJobApplication>,
) => {
    setValue("status", jobApplication.status);
    setValue("internalNote", jobApplication.internalNote);
};

export type UpdateJobApplicationControlProps = {
    initialLoading: boolean,
    loading: boolean,
    submitHandler: Fn,
};

type Props = {
    route: Route,
    jobApplication: JobApplicationDetails | undefined,
    setJobApplication: SetStateFn<JobApplicationDetails | undefined>,
};

const JobApplicationEditor = ({route, jobApplication, setJobApplication}: Props) => {
    const intl = useIntl();
    const [showDetailsSection, setShowDetailsSection] = useState(false);
    const {breakpoint} = useResponsive();
    const {notify} = useNotifications();
    const isLargeScreen = useMemo(() => breakpointEqualOrGreaterThan(breakpoint, "xl"), [breakpoint]);
    const {mutate, isPending} = useMutation({
        mutationFn: (updatedFields: UpdatedJobApplication) => {
            if (!jobApplication)
                throw Error();

            return api.jobApplication.updateJobApplication(jobApplication.id, updatedFields)
        },
        onSuccess: (result: JobApplicationDetails) => {
            setJobApplication(result);
            notifySuccess(intl.formatMessage({id: "saved"}), notify)
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify)
    });
    const formMethods = useForm<UpdatedJobApplication>({
        defaultValues: {status: "OPEN"},
    });
    const submitHandler: Fn = formMethods.handleSubmit((data: UpdatedJobApplication) => {
        mutate(data);
    });

    // Set initial form values
    useEffect(() => {
        if (!jobApplication)
            return;

        setJobApplicationValues(jobApplication, formMethods.setValue);
    }, [jobApplication, formMethods.setValue]);

    return (
        <FormProvider {...formMethods}>
            <JobApplicationHeading
                route={route}
                setShowDetailsSection={setShowDetailsSection}
                isLargeScreen={isLargeScreen}
            />

            <div className={"mt-6 w-full flex flex-row items-start justify-start gap-6 md:gap-12"}>
                <div className={"w-full flex flex-col items-center justify-start gap-6"}>
                    <JobApplicationMotivation jobApplication={jobApplication}/>

                    <JobApplicationInternalNote
                        initialLoading={jobApplication === undefined}
                        loading={isPending}
                        submitHandler={submitHandler}
                    />
                </div>

                <JobApplicationFormDetails
                    isLargeScreen={isLargeScreen}
                    showDetailsSection={showDetailsSection}
                    setShowDetailsSection={setShowDetailsSection}
                    jobApplication={jobApplication}
                    initialLoading={jobApplication === undefined}
                    loading={isPending}
                    submitHandler={submitHandler}
                />
            </div>
        </FormProvider>
    );
};

export default JobApplicationEditor;